import GuideText from 'components/v2/GuideText';
import { toaster } from 'components/v2/toast';
import { ProcessStatus } from 'constants/constants';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useState } from 'react';
import { QUERY_KEYS } from 'services/api/constants';
import { useCreateCollocationCenter, useUpdateCollocationCenter } from 'services/api/mutations';
import { useCollocationCenters, useCountriesLookup } from 'services/api/queries';
import styled from 'styled-components';
import { ButtonSmall } from 'styles/Styles.styled';
import { CollocationCenterModel, Option, UpdateCollocationCenterModel } from 'types/types';
import queryClient from 'util/queryClient';

import AddDialog from './AddCollocationCenterDialog';
import CollocationCentersList from './CollocationCentersList';
import EditDialog from './EditCollocationCenterDialog';
import EmptyCollocationCentersList from './EmptyCollocationCentersList';
import { uploadCollocationCenterLogo } from 'services/api/epdCollocationCentersApi';

const ManageCollocationCenters = () => {
  const [status, setStatus] = useState<ProcessStatus>(ProcessStatus.None);
  const [showAddDialog, setShowAddDialog] = useState<boolean>(false);
  const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
  const [selectedCollocationCenter, setSelectedCollocationCenter] = useState<CollocationCenterModel | null>(null);

  const collocationCenters = useCollocationCenters().data;
  const createMutation = useCreateCollocationCenter();
  const updateMutation = useUpdateCollocationCenter();

  const countriesLookup = (useCountriesLookup().data || []).map((country) => {
    return { value: country.alpha2Code, label: country.name } as Option;
  });

  const handleEditCollocationCenter = (collocationCenter: CollocationCenterModel) => {
    setSelectedCollocationCenter(collocationCenter);
    setShowEditDialog(true);
  };

  const createCollocationCenter = async (values: any) => {
    const model: UpdateCollocationCenterModel = {
      id: null,
      name: values.name,
      description: values.description,
      countryCodes: values.countries.map((i: any) => i.value),
      logotypeUrl: null,
      removeLogo: false,
    };

    setStatus(ProcessStatus.Fetching);

    createMutation.mutate(model, {
      onSuccess: (result) => {
        const createdId = result.data;
        if (values.logo && values.logo instanceof File && createdId) {
          uploadCollocationCenterLogo(createdId, values.logo)
            .then(() => queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COLLOCATION_CENTER] }))
            .catch((error) => {
              console.error('Error uploading logo:', error);
              toaster({ severity: 'error', summary: 'Error', details: 'Logo upload failed' });
              throw error;
            });
        }

        setStatus(ProcessStatus.Success);
        setShowAddDialog(false);
        toaster({
          severity: 'success',
          summary: 'Success',
          details: 'Collocation center has been created',
        });
      },
      onError: (error: any) => {
        toaster({ severity: 'error', summary: 'Error', details: 'Collocation center creation error' });
        console.error(error);
        setStatus(ProcessStatus.Error);
      },
    });
  };

  const updateCollocationCenter = async (values: any) => {
    const model: UpdateCollocationCenterModel = {
      id: selectedCollocationCenter?.id ?? null,
      name: values.name,
      description: values.description,
      countryCodes: values.countries.map((i: any) => i.value),
      removeLogo: values.logo === null,
      logotypeUrl: selectedCollocationCenter?.logotypeUrl ?? null,
    };

    setStatus(ProcessStatus.Fetching);

    updateMutation.mutate(model, {
      onSuccess: async () => {
        if (values.logo && values.logo instanceof File && selectedCollocationCenter?.id) {
          uploadCollocationCenterLogo(selectedCollocationCenter?.id, values.logo)
            .then(() => queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COLLOCATION_CENTER] }))
            .catch((error) => {
              console.error('Error uploading logo:', error);
              toaster({ severity: 'error', summary: 'Error', details: 'Logo upload failed' });
              throw error;
            });
        }

        setStatus(ProcessStatus.Success);
        setShowEditDialog(false);
        toaster({ severity: 'success', summary: 'Success', details: 'Collocation center has been updated' });
      },
      onError: (error: any) => {
        toaster({ severity: 'error', summary: 'Error', details: 'Collocation center update error' });
        console.error(error);
        setStatus(ProcessStatus.Error);
      },
    });
  };

  if (status === ProcessStatus.Fetching) {
    return <ProgressSpinner />;
  }

  return (
    <>
      <ConfirmDialog />
      <div className="flex justify-content-between w-full py-2">
        <PageTitle>Collocation centers</PageTitle>
        <ButtonSmall
          style={{ paddingLeft: '1.4rem', paddingRight: '1.4rem', alignSelf: 'center' }}
          onClick={() => setShowAddDialog(true)}
        >
          Add collocation center
        </ButtonSmall>
        <AddDialog
          show={showAddDialog}
          onHide={() => setShowAddDialog(false)}
          onSubmit={createCollocationCenter}
          countriesLookup={countriesLookup}
        />
        <EditDialog
          show={showEditDialog}
          item={selectedCollocationCenter!}
          onHide={() => setShowEditDialog(false)}
          onSubmit={updateCollocationCenter}
          countriesLookup={countriesLookup}
        />
      </div>

      <GuideText value={'Here you can manage collocation centers.'} />

      {collocationCenters && collocationCenters.length > 0 ? (
        <CollocationCentersList
          collocationCenters={collocationCenters}
          handleEditCollocationCenter={handleEditCollocationCenter}
        />
      ) : (
        <EmptyCollocationCentersList />
      )}
    </>
  );
};

const PageTitle = styled.div`
  font-style: normal;
  font-weight: 700;
  font-size: 28px;
  line-height: 34px;
  color: #151515;
`;

export default ManageCollocationCenters;
